import { useTranslation } from 'next-i18next'
import { FC, useCallback, useMemo } from 'react'

import Product from '@src/quince-site-common/src/domains/Product'
import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductOptionDisplayType, ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { getFormattedAverageRating } from '@src/quince-site-common/src/utils/getFormattedAverageRating'
import OptionSelector from '@src/quince-site-pdp/src/atoms/optionSelector/OptionSelector'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import { Icon } from '../../../atoms'
import { TrackConfig } from '../../../hocs/withTracking'
import FilledStar from '../icons/filledStar.inline.svg'
import { useProductCardContext } from '../product-card-context/product-card.context'
import { getFilteredOptionValueList } from '../productCard.helpers'

import styles from './product-card-footer.module.scss'

export const OPTION_DISPLAY_TYPES_VS_CLASS_NAME = {
  [ProductOptionDisplayType.COLOR]: styles.colorOptionProductCard,
  [ProductOptionDisplayType.IMAGE]: styles.colorOptionProductCard,
}

function BottomTagComponent({ isWhiteGloveDelivery }: { isWhiteGloveDelivery?: boolean }) {
  const { t } = useTranslation('common')

  if (isWhiteGloveDelivery) {
    return <div className={styles.whiteglove}>{t('free_white_glove_delivery')}</div>
  }

  return null
}

export interface ProductCardFooterProps {
  hideReviews?: boolean
  hideOptions?: boolean
  shouldShowMore?: boolean
  optionLimit?: number
  onShowMoreClick?: (params: { selectedOptions: ProductVariantOption[] }) => void
  productViewTracker?: string
  applicableTags?: ProductTag[]
  productClickTrackConfig?: TrackConfig
  isWhiteGloveDelivery?: boolean
}

const PRODUCT_OPTION_NAME = 'Color'

export const ProductCardFooter: FC<ProductCardFooterProps> = ({
  hideReviews,
  hideOptions,
  shouldShowMore,
  optionLimit,
  onShowMoreClick,
  productViewTracker,
  applicableTags,
  productClickTrackConfig,
  isWhiteGloveDelivery,
}) => {
  const { product, selectedOptions, setSelectedOptions } = useProductCardContext()

  const productOptions = useMemo(
    () =>
      product.type === PRODUCT_TYPES.BUNDLE
        ? product.productOptions
        : productHelper.getCascadingProductOptions(product, []),
    [product],
  )

  const formattedAvgRating = getFormattedAverageRating(product.productReviewSummary?.averageRating)
  const selectedOptionValue = selectedOptions[0]?.value

  const optionValueList = useMemo(
    () =>
      getFilteredOptionValueList(
        productHelper.getProductOptionToDisplayInProductCard(productOptions)?.values ?? [],
        selectedOptionValue ?? '',
      ),
    [productOptions, selectedOptionValue],
  )

  const onOptionSelect = useCallback(
    (optionValue: string) => {
      setSelectedOptions([
        new ProductVariantOption({
          name: productOptions[0]?.name ?? ProductOptionName.Color,
          value: optionValue,
        }),
      ])
      if (productViewTracker) {
        const color =
          (productOptions[0]?.name === ProductOptionName.Color
            ? optionValue
            : selectedOptions.find(so => so.name === ProductOptionName.Color)?.value) ?? null
        const tags = Array.from(
          new Set(
            [...(applicableTags ?? []), ...product.tags]
              .filter(tag => tag.options.some(opt => opt.value === color) || tag.options.length === 0)
              .map(tag => tag.value),
          ),
        )
        const selectedVariants = productHelper.getVariantsForOptions(product, selectedOptions)
        trackEvent(TELEMETRY_EVENT_NAMES.VARIANT_SELECT, {
          color,
          product_id: product.spid,
          internalVariantIds: selectedVariants.map(sv => sv.id),
          variantIds: selectedVariants.map(sv => sv.svid),
          product_options: selectedOptions,
          product_options_all: product.productOptions,
          tags,
          tag_ids: transformTagsIntoAnalyticsTags(tags),
          stock_info: null,
          entity_id: product.id,
          entity_type: 'product_id',
          productType: (product as Product).productTypeName,
          productTitle: product.title,
          variantTitle: selectedVariants[0]?.title,
          price: selectedVariants[0]?.price,
          is_product_card: true,
          division: product.classification?.division?.name,
          department: product.classification?.department?.name,
          subdepartment: product.classification?.subDepartment?.name,
          product_option_name: PRODUCT_OPTION_NAME,
          product_option_value: optionValue,
          element_name: productClickTrackConfig?.context?.contentContainerName || null,
          element_display_name: productClickTrackConfig?.context?.contentContainerDisplayName || null,
          element_type: productClickTrackConfig?.context?.contentContainerType || null,
          element_id: productClickTrackConfig?.context?.trackingId || null,
          element_position: productClickTrackConfig?.context?.contentContainerPosition || null,
          context: {
            productProperties: getProductPropertiesForTracking(
              product as Product,
              selectedVariants[0]?.svid?.toString(),
            ),
          },
        })
      }
    },
    [
      productOptions,
      applicableTags,
      product,
      productClickTrackConfig,
      productViewTracker,
      selectedOptions,
      setSelectedOptions,
    ],
  )

  const isColorOption = optionValueList.some(
    option =>
      option.displayType === ProductOptionDisplayType.COLOR || option.displayType === ProductOptionDisplayType.IMAGE,
  )

  const showMoreOption = isColorOption && shouldShowMore

  const handleShowMoreClick = useCallback(() => {
    onShowMoreClick?.({ selectedOptions })
  }, [onShowMoreClick, selectedOptions])

  return (
    <div className={styles.productCardFooter}>
      {!hideReviews && formattedAvgRating && (
        <div className={styles.rating}>
          <Icon className={styles.rating_icon} iconSvg={FilledStar} />
          <span className={styles.rating_text}>{formattedAvgRating}</span>
        </div>
      )}
      {!hideOptions && !productOptions[0]?.isHidden && !getShouldHideOptions(product.id) && (
        <OptionSelector
          optionDisplayTypesVsClassName={OPTION_DISPLAY_TYPES_VS_CLASS_NAME}
          optionValueList={optionValueList}
          className={styles.colorOptionWrapper}
          optionWrapperClassName={styles.colorOptionWrapper__optionWrapper}
          selectedValue={selectedOptions[0]?.value}
          isAllRoundOptions
          onSelect={onOptionSelect}
          showMoreOption={showMoreOption}
          optionLimit={optionLimit}
          onShowMoreClick={handleShowMoreClick}
        />
      )}
      <BottomTagComponent isWhiteGloveDelivery={isWhiteGloveDelivery} />
    </div>
  )
}

const getShouldHideOptions = (productId: number | string) => {
  const HIDE_OPTIONS_PRODUCT_IDS = new Set(['44'])
  return HIDE_OPTIONS_PRODUCT_IDS.has(String(productId))
}
